/**
 * Waves Autotune session state - the ONE selected key + scale type,
 * shared across every WavesAutotune item on the page.
 *
 * Mirrors `transpose/session.ts`: a single selection lives here (not on the
 * item), so multiple WavesAutotune items stay in lock-step and survive item
 * remounts. Selecting a key or scale persists it per-device and dispatches
 * the matching MIDI CC to the primary bridge's default output.
 *
 * This is deliberately independent of the concert transpose - changing the
 * autotune key does NOT move Ableton's transpose, and vice-versa (user
 * decision 2026-07-16). The two are separate live controls.
 *
 * The CC numbers and the learned per-key / per-scale CC values are fixed
 * properties of the user's Waves plugin mapping, so they live here as
 * constants. The MIDI channel is per-item now (configurable in the
 * Inspector) and is passed in to the setters - so `setAutotuneKey` /
 * `setAutotuneScale` dispatch on the tapped item's channel. If the CC
 * mapping is re-learned, edit the tables below.
 */

import { createSignal } from 'solid-js';
import { sendAutotuneCc, fanoutTargets } from '../opbridge/dispatch.ts';

/** Default MIDI channel (1-16) for a fresh Waves Autotune item. */
export const DEFAULT_AUTOTUNE_CHANNEL = 2;
/** CC number that selects the key. */
export const AUTOTUNE_KEY_CC = 55;
/** CC number that selects the scale. NOT a two-state Min/Maj switch - it
 *  indexes Waves Tune's full SCALE LIST, which is 44 entries long:
 *
 *      1 User Scale · 2 Chromatic · 3 Major · 4 Natural Minor
 *      5 Harmonic Minor · 6 Melodic Minor (Asc) · 7 Melodic Minor (Asc & Desc)
 *      8 Major Justonic · 9 Minor Justonic · 10 Dorian · 11 Mixolydian
 *      12 Blues · 13 Phrygian · 14 Locrian · 15 Lydian · ... · 44 Asavari (Carnatic)
 *
 *  The 44 is no longer an estimate: the operator photographed the whole
 *  dropdown 2026-09-11 and it ends at 44 Asavari (Carnatic) exactly.
 *
 *  The CC spans 0-127 across those 44 entries, so each is ~2.909 wide. That
 *  is why every value here is a small number only a few apart: they are
 *  adjacent list positions near the top, not a scale of intensities.
 *
 *  MEASURED 2026-09-01 on the real plugin, six points (3, 7, 10, 13, 15 and
 *  a D-major round trip). Do not derive these; the width is narrow enough
 *  that being one out lands on a completely different scale. */
export const AUTOTUNE_SCALE_CC = 56;
/** CC number for tune strength, "Auto Tune Strength" on ch 2. Continuous
 *  0-127, unlike everything else here, so it is driven by a fader rather
 *  than the key/scale button rows. Added 2026-09-01. */
export const AUTOTUNE_STRENGTH_CC = 6;

/** Note name per pitch class (0=C .. 11=B), spelled to match the user's
 *  TouchOSC panel (Eb/Ab/Bb flats, C#/F# sharps). */
export const NOTE_NAMES = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'] as const;

/** Learned CC-55 value per pitch class - the user's Waves MIDI-learn map.
 *  Irregular breakpoints (not linear), so stored explicitly.
 *
 *  MEASURED AND CONFIRMED 2026-09-01 against the real plugin (Waves Tune
 *  Real-Time Mono, on the Mac, over `OPBridge-Ableton` ch 2): 23 -> D,
 *  7 -> C#, 60 -> F#. Do NOT "correct" any of these from a formula.
 *
 *  A session that day derived an even-12-zone model from these numbers,
 *  found 7 and 60 apparently one zone low, and nearly filed them as a live
 *  defect. The rig disagreed. The premise is in fact unsatisfiable: 7 -> C#
 *  needs zones narrower than ~7 while 23 -> D needs them wider than ~7.7,
 *  so no zone count or range fits both. These values came from MIDI-learn
 *  against a plugin whose mapping is simply not uniform, and the table is
 *  the only authority. */
export const KEY_CC_VALUES: Record<number, number> = {
  0: 3,    // C
  1: 7,    // C#
  2: 23,   // D
  3: 40,   // Eb
  4: 44,   // E
  5: 55,   // F
  6: 60,   // F#
  7: 77,   // G
  8: 92,   // Ab
  9: 100,  // A
  10: 116, // Bb
  11: 125, // B
};

/** `min` is NATURAL minor. Deliberately not renamed to `minNat` when
 *  harmonic minor was added: this string is the key OPBridge looks up in the
 *  published `scale_values` doc when OPSetlist sends a song whose key ends
 *  `m`/`min`, so renaming it would break their lookup for zero gain. */
export type AutotuneScale = 'min' | 'minHarm' | 'maj' | 'chrom' | 'dorian';

/** Learned CC-56 values - all four are positions in the 44-entry scale list
 *  described on `AUTOTUNE_SCALE_CC`, which is why they are small and only a
 *  few apart rather than spread across 0-127.
 *
 *  MEASURED on the plugin 2026-09-01, every one of them read off the Waves
 *  UI: 3 -> Chromatic, 7 -> Major, 10 -> Natural Minor, 13 -> Harmonic Minor.
 *
 *  DO NOT recompute these. Each entry is ~2.909 wide, so being one out
 *  selects a neighbouring SCALE, not a near-miss: below Chromatic is the
 *  user's own custom scale, and above Harmonic Minor is Melodic Minor
 *  (Ascending). An earlier pass this same day moved `chrom` onto its own
 *  CC 57 after reading the Matrix label "Waves Tuner Chromatic" and assuming
 *  CC 56 was a two-state Min/Maj switch. The plugin disproved it: chromatic
 *  is entry 2 of this list and has always been reachable here. What CC 57
 *  does is still unknown, and nothing depends on it.
 *
 *  `dorian` (28) REACHED THIS TABLE DIFFERENTLY FROM THE OTHER FOUR, and the
 *  route is worth keeping because it is the only one DEC-196 permits. The
 *  other four are direct MIDI-learn measurements. This one was DERIVED first
 *  - Dorian is entry 10 of 44 in the dropdown the operator photographed
 *  2026-09-11, so its band is [26.18, 29.09) and 28 is the integer with the
 *  largest margin to either boundary (1.82 below, 1.09 above; 27 would leave
 *  only 0.82 below) - and then CONFIRMED ON THE PLUGIN the same day, operator
 *  verbatim: *"dor is working good."* A derivation alone must never ship; a
 *  derivation ratified by a read-back is a measurement.
 *
 *  The derivation was defensible only because all four measured points fit
 *  the same model - 3/7/10/13 land inside the bands for entries 2/3/4/5 -
 *  which is precisely what the KEY table below could NOT say about itself.
 *  Do not read this as licence to derive a KEY value. */
export const SCALE_CC_VALUES: Record<AutotuneScale, number> = {
  chrom: 3,
  maj: 7,
  min: 10,
  minHarm: 13,
  dorian: 28,
};

/** Pitch class of A (row-1 default home → "Am"). */
export const PITCH_A = 9;
/** Pitch class of E (row-2 default home → "Em"). */
export const PITCH_E = 4;

/** Note name for a pitch class, normalised into 0-11. */
export function noteName(pitchClass: number): string {
  const pc = (((Math.round(pitchClass) % 12) + 12) % 12);
  return NOTE_NAMES[pc] ?? '';
}

const KEY_STORE = 'opbuttons.autotuneKey.v1';
const SCALE_STORE = 'opbuttons.autotuneScale.v1';
const STRENGTH_STORE = 'opbuttons.autotuneStrength.v1';

function loadKey(): number | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(KEY_STORE);
  if (raw === null) return null;
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 0 || n > 11) return null;
  return n;
}

function loadScale(): AutotuneScale | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(SCALE_STORE);
  if (raw === 'min' || raw === 'minHarm' || raw === 'maj' || raw === 'chrom' || raw === 'dorian') return raw;
  return null;
}

function loadStrength(): number | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(STRENGTH_STORE);
  if (raw === null) return null;
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 0 || n > 127) return null;
  return n;
}

function persist(key: string, v: string): void {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.setItem(key, v); } catch { /* private mode / quota */ }
}

const [keySig, setKeySig] = createSignal<number | null>(loadKey());
/** The selected autotune key as a pitch class (0-11), or null if none
 *  selected yet. What a WavesAutotune row's active cell reflects. */
export const autotuneKey = keySig;

const [scaleSig, setScaleSig] = createSignal<AutotuneScale | null>(loadScale());
/** The selected autotune scale type, or null if none selected yet. */
export const autotuneScale = scaleSig;

const [strengthSig, setStrengthSig] = createSignal<number | null>(loadStrength());
/** Tune strength 0-127, or null if never set on this device. Null renders
 *  as an untouched fader rather than as 0 - "off" and "not yet set" are
 *  different, and defaulting to 0 would silently disable correction. */
export const autotuneStrength = strengthSig;

/**
 * Select the autotune key (pitch class, normalised 0-11), persist it, and
 * dispatch CC 55 with the learned value on the given MIDI channel. Never
 * throws; an offline target no-ops.
 *
 * `bridgeIds` is the CALLING item's own multi-select bridge affinity
 * (2026-08-13) — same pattern as `transpose/session.ts`'s
 * `setConcertTranspose`: the key/scale VALUE is session-global, but each
 * WavesAutotune row fans its own dispatch out to its own configured
 * bridge(s). Absent resolves to the default bridge.
 */
export function setAutotuneKey(pitchClass: number, channel: number, bridgeIds?: string[]): void {
  const pc = (((Math.round(pitchClass) % 12) + 12) % 12);
  setKeySig(pc);
  persist(KEY_STORE, String(pc));
  setLastKeySendSig({ value: pc, at: Date.now() });
  for (const target of fanoutTargets(bridgeIds)) {
    void sendAutotuneCc(channel, AUTOTUNE_KEY_CC, KEY_CC_VALUES[pc] ?? 0, target);
  }
}

/**
 * The last autotune key WE actually dispatched, with when. `null` until this
 * page sends one.
 *
 * Exactly the same shape and the same reason as `lastTransposeSend` in
 * `transpose/session.ts` - read that note for the full story. In short: the
 * key row's `pending` used to be inferred from `autotuneKey()`, a latch
 * hydrated from `localStorage` at boot, so once the LIT cell moved to the
 * setlist readback the two disagreed on almost every song and a cell nobody
 * pressed claimed to be an unconfirmed send.
 *
 * ⚠️ THIS ONE WAS MISSED when transpose and capo were fixed on 2026-09-21,
 * and it was found by enumerating every remaining reader of the latches
 * rather than by the operator reporting a second phantom. Worth recording:
 * the fix for a defect class is not done when the reported instances are
 * fixed, it is done when the enumeration comes back empty.
 *
 * A send stops being pending when the readback AGREES, or when a readback
 * NEWER than the send DISAGREES - the rig moved on without us. No timeout,
 * deliberately: with no setlist authority `observedKey()` is permanently null
 * and this is then the only honest feedback that a tap did anything.
 */
const [lastKeySendSig, setLastKeySendSig] = createSignal<{ value: number; at: number } | null>(null);
export const lastAutotuneKeySend = lastKeySendSig;

/** Drop the recorded send. Tests + sign-out. */
export function resetLastAutotuneKeySend(): void {
  setLastKeySendSig(null);
}

/** The last autotune SCALE we dispatched, with when. Same reason as the key's:
 *  the latch is hydrated from localStorage and is not a send. See
 *  `autotune/readback.ts` for how it is ordered against a song change. */
const [lastScaleSendSig, setLastScaleSendSig] = createSignal<{ value: AutotuneScale; at: number } | null>(null);
export const lastAutotuneScaleSend = lastScaleSendSig;

/**
 * Select the autotune scale type, persist it, and dispatch CC 56 with the
 * learned value on the given MIDI channel. See `setAutotuneKey` for the
 * `bridgeIds` contract.
 */
export function setAutotuneScale(scale: AutotuneScale, channel: number, bridgeIds?: string[]): void {
  setScaleSig(scale);
  persist(SCALE_STORE, scale);
  setLastScaleSendSig({ value: scale, at: Date.now() });
  // ONE destination for all four modes. They are all positions in the same
  // scale list on CC 56 - see SCALE_CC_VALUES for why an earlier pass split
  // chromatic onto CC 57 and why the plugin disproved it.
  for (const target of fanoutTargets(bridgeIds)) {
    void sendAutotuneCc(channel, AUTOTUNE_SCALE_CC, SCALE_CC_VALUES[scale], target);
  }
}

/**
 * Set tune strength (0-127) and dispatch `AUTOTUNE_STRENGTH_CC`. Continuous,
 * so unlike key/scale this is driven by a fader drag and can fire often -
 * callers should throttle if they are dispatching per pointer-move.
 *
 * Session-wide like key and scale, so multiple WavesAutotune items stay in
 * lock-step. See `setAutotuneKey` for the `bridgeIds` contract.
 */
export function setAutotuneStrength(value: number, channel: number, bridgeIds?: string[]): void {
  const v = Math.max(0, Math.min(127, Math.round(value)));
  setStrengthSig(v);
  persist(STRENGTH_STORE, String(v));
  for (const target of fanoutTargets(bridgeIds)) {
    void sendAutotuneCc(channel, AUTOTUNE_STRENGTH_CC, v, target);
  }
}
